import { useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { ADDRESS_FIELDS } from 'constants/address-structure';
import { countries } from 'constants/countries';
import FeatherIcon from 'feather-icons-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { CollapsibleBlock } from 'components/CollapsibleBlock/CollapsibleBlock';
import { Button, Input, InputGroup } from 'components/FormComponents';

import styles from './CandidateCheckValues.module.scss';

export const CandidateCheckValues = ({ checkValues, updateCandidateChecksValues = () => {} }) => {
  const [valuesKeys, setValuesKeys] = useState([]);

  const [valueToUpdate, setValueToUpdate] = useState(false);

  useEffect(() => {
    if (checkValues?.values && typeof checkValues.values === 'object') {
      const metadata = checkValues?.admin_field_metadata || {};

      // Filter out fields that should be hidden
      const filteredKeys = Object.keys(checkValues.values).filter(key => {
        const fieldMeta = metadata[key];
        // If no metadata, show the field (unknown field)
        if (!fieldMeta) return true;
        // Hide if marked as hide
        return !fieldMeta.hide;
      });

      // Sort by order from metadata
      const sortedKeys = filteredKeys.sort((keyA, keyB) => {
        const metaA = metadata[keyA];
        const metaB = metadata[keyB];

        const orderA = metaA?.order ?? Infinity;
        const orderB = metaB?.order ?? Infinity;

        // Sort by order first
        if (orderA !== orderB) {
          return orderA - orderB;
        }

        // Same order - sort alphabetically
        return keyA.localeCompare(keyB);
      });

      setValuesKeys(sortedKeys);
    } else {
      setValuesKeys([]);
    }
  }, [checkValues]);

  const onUpdateValues = (name, value) => {
    setValueToUpdate();
    updateCandidateChecksValues({ ...checkValues.values, [name]: value });
  };

  const addressFieldCodes = useMemo(() => {
    const set = new Set();
    const metadata = checkValues?.admin_field_metadata || {};
    Object.entries(metadata).forEach(([code, meta]) => {
      if (meta?.type === 'address') {
        set.add(code.toLowerCase());
      }
    });
    return set;
  }, [checkValues?.admin_field_metadata]);

  const countryAlpha2FieldCodes = useMemo(() => {
    const set = new Set();
    const metadata = checkValues?.admin_field_metadata || {};
    Object.entries(metadata).forEach(([code, meta]) => {
      if (meta?.type === 'country_alpha2') {
        set.add(code.toLowerCase());
      }
    });
    return set;
  }, [checkValues?.admin_field_metadata]);

  const addressFieldNames = useMemo(() => new Set(ADDRESS_FIELDS.map(f => f.fieldName)), []);

  const isAddressLikeObject = obj => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
    const keys = Object.keys(obj);
    const addressKeyCount = keys.filter(k => addressFieldNames.has(k)).length;
    return addressKeyCount >= 2;
  };

  const renderAddressLine = addressObj => {
    if (!addressObj || typeof addressObj !== 'object') return null;

    const orderedKeys = ADDRESS_FIELDS.map(field => field.fieldName);

    const parts = (orderedKeys || [])
      .map(key => addressObj[key])
      .filter(Boolean)
      .filter((value, index, self) => {
        const previousNonEmptyIndex = self.slice(0, index).findLastIndex(v => v);
        if (previousNonEmptyIndex === -1) return true;
        return value !== self[previousNonEmptyIndex];
      });

    return parts.join(', ');
  };

  const getCopyableText = value => {
    if (value === null || value === undefined) return String(value ?? '');
    if (typeof value !== 'object') return String(value);
    if (isAddressLikeObject(value)) return renderAddressLine(value) || '';
    if (Array.isArray(value) && value.length > 0 && value.every(isAddressLikeObject)) {
      return value.map(renderAddressLine).filter(Boolean).join('\n');
    }
    return JSON.stringify(value, null, 2);
  };

  const onCopy = v => {
    navigator.clipboard.writeText(getCopyableText(v));
  };

  const renderValue = (value, parentKey = '') => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'true' : 'false';

    switch (typeof value) {
      case 'object':
        if (Array.isArray(value)) {
          if (typeof value[0] === 'string') {
            return (
              <ul>
                {value.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            );
          }
          return value.map((obj, i) => {
            if (!obj) return null;
            const isAddress = isAddressLikeObject(obj);
            const addressLine = isAddress ? renderAddressLine(obj) : null;
            return (
              <CollapsibleBlock key={i} className={styles.addressBlock}>
                <CollapsibleBlock.Header>
                  <div className={styles.addressHeaderRow}>
                    {addressLine ? (
                      <>
                        <OverlayTrigger overlay={<Tooltip>Copy address</Tooltip>}>
                          <span
                            className={styles.copyIconWrap}
                            onClick={e => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(addressLine || '');
                            }}>
                            <FeatherIcon icon="copy" size={14} />
                          </span>
                        </OverlayTrigger>
                        <span className={cn('primary-text u-font-size-small', styles.addressLine)}>
                          {addressLine}
                        </span>
                      </>
                    ) : (
                      <span className="primary-text u-font-size-small u-capitalize">
                        {parentKey.replaceAll('_', ' ')} {i + 1}
                      </span>
                    )}
                  </div>
                </CollapsibleBlock.Header>
                <CollapsibleBlock.Content>
                  <ul key={i} className="u-margin-top">
                    {Object.keys(obj || {}).map(key => (
                      <li key={key}>
                        <span className={styles.key}>{key.replaceAll('_', ' ')}</span> :{' '}
                        {renderValue(obj[key], key)}
                      </li>
                    ))}
                  </ul>
                </CollapsibleBlock.Content>
              </CollapsibleBlock>
            );
          });
        }
        if (addressFieldCodes.has(parentKey?.toLowerCase()) || isAddressLikeObject(value)) {
          const addressText = renderAddressLine(value);
          return (
            <>
              <CollapsibleBlock className={styles.addressBlock}>
                <CollapsibleBlock.Header>
                  <div className={styles.addressHeaderRow}>
                    <OverlayTrigger overlay={<Tooltip>Copy address</Tooltip>}>
                      <span
                        className={styles.copyIconWrap}
                        onClick={e => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(addressText || '');
                        }}>
                        <FeatherIcon icon="copy" size={14} />
                      </span>
                    </OverlayTrigger>
                    <span className={cn('primary-text u-font-size-small', styles.addressLine)}>
                      {addressText}
                    </span>
                  </div>
                </CollapsibleBlock.Header>
                <CollapsibleBlock.Content>
                  <ul className="u-margin-left u-margin-top">
                    {Object.keys(value || {}).map(key => (
                      <li key={key}>
                        <span className={styles.key}>{key.replaceAll('_', ' ')}</span> :{' '}
                        {renderValue(value[key], key)}
                      </li>
                    ))}
                  </ul>
                </CollapsibleBlock.Content>
              </CollapsibleBlock>
            </>
          );
        }

        return (
          <ul>
            {Object.keys(value || {}).map(key => (
              <li key={key}>
                <span className={styles.key}>{key.replaceAll('_', ' ')}</span> :{' '}
                {renderValue(value[key], key)}
              </li>
            ))}
          </ul>
        );
      default:
        if (
          parentKey &&
          typeof parentKey === 'string' &&
          countryAlpha2FieldCodes.has(parentKey.toLowerCase()) &&
          value
        ) {
          const countryName = countries.find(c => c.alpha2 === value)?.name;
          return countryName ? `${value} (${countryName})` : value;
        }
        return value;
    }
  };

  return (
    <table className={styles.root}>
      {valueToUpdate && (
        <UpdateValueModal
          visible={true}
          value={valueToUpdate}
          onClose={() => setValueToUpdate()}
          onSubmit={onUpdateValues}
        />
      )}
      <tbody>
        {valuesKeys.map(key => (
          <tr key={key} className={styles.valuesItem}>
            <td className={styles.key} onClick={() => onCopy(checkValues.values[key])}>
              {key.replaceAll('_', ' ')}
            </td>
            <td
              className={cn(styles.value, 'd-flex flex-column u-gap')}
              onClick={() => onCopy(checkValues.values[key])}>
              {renderValue(checkValues.values[key], key)}
            </td>
            <td>
              <OverlayTrigger overlay={<Tooltip>Copy value</Tooltip>}>
                <div style={{ display: 'inline-block' }} className="u-margin-right">
                  <FeatherIcon
                    icon="copy"
                    size={14}
                    onClick={() => onCopy(checkValues.values[key])}
                  />
                </div>
              </OverlayTrigger>
              <OverlayTrigger overlay={<Tooltip>Edit value</Tooltip>}>
                <div style={{ display: 'inline-block' }} className="u-margin-right">
                  <FeatherIcon
                    icon="edit"
                    size={14}
                    onClick={() => setValueToUpdate({ name: key, value: checkValues.values[key] })}
                  />
                </div>
              </OverlayTrigger>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const UpdateValueModal = ({ visible, value, onClose = () => {}, onSubmit = () => {} }) => {
  const isObject =
    typeof value.value === 'object' && value.value !== null && !Array.isArray(value.value);

  const isArrayOfObjects =
    Array.isArray(value.value) && value.value.every(v => typeof v === 'object' && v !== null);

  const [updatedValue, setUpdatedValue] = useState(isObject || isArrayOfObjects ? '' : value.value);
  const [updatedFields, setUpdatedFields] = useState(isObject ? value.value : {});
  const [updatedArrayFields, setUpdatedArrayFields] = useState(isArrayOfObjects ? value.value : []);

  return (
    <AnimatedModal
      title={`Update '${value.name.replaceAll('_', ' ')}'`}
      visible={visible}
      onClose={onClose}>
      {isObject && (
        <>
          {Object.entries(updatedFields).map(([key, val]) => (
            <InputGroup key={key} title={key.replaceAll('_', ' ')}>
              <Input
                inputProps={{
                  value: val,
                  onChange: e =>
                    setUpdatedFields(prev => ({
                      ...prev,
                      [key]: e.target.value,
                    })),
                }}
              />
            </InputGroup>
          ))}
        </>
      )}

      {isArrayOfObjects && (
        <>
          {updatedArrayFields.map((obj, index) => (
            <div key={index} className="u-margin-bottom">
              <h4 className="u-margin-top">Item {index + 1}</h4>
              {Object.entries(obj).map(([key, val]) => {
                if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
                  return (
                    <div key={key} className="u-margin-bottom">
                      <h5>{key.replaceAll('_', ' ')}</h5>
                      {Object.entries(val).map(([subKey, subVal]) => (
                        <InputGroup key={subKey} title={subKey.replaceAll('_', ' ')}>
                          <Input
                            inputProps={{
                              value: subVal,
                              onChange: e => {
                                const updated = [...updatedArrayFields];
                                updated[index] = {
                                  ...updated[index],
                                  [key]: {
                                    ...updated[index][key],
                                    [subKey]: e.target.value,
                                  },
                                };
                                setUpdatedArrayFields(updated);
                              },
                            }}
                          />
                        </InputGroup>
                      ))}
                    </div>
                  );
                }

                return (
                  <InputGroup key={key} title={key.replaceAll('_', ' ')}>
                    <Input
                      inputProps={{
                        value: val,
                        onChange: e => {
                          const updated = [...updatedArrayFields];
                          updated[index] = {
                            ...updated[index],
                            [key]: e.target.value,
                          };
                          setUpdatedArrayFields(updated);
                        },
                      }}
                    />
                  </InputGroup>
                );
              })}
            </div>
          ))}
        </>
      )}

      {!isObject && !isArrayOfObjects && (
        <InputGroup title="Value" className="u-margin-top">
          <Input
            inputProps={{
              value: updatedValue,
              onChange: e => setUpdatedValue(e.target.value),
            }}
          />
        </InputGroup>
      )}

      <Button
        className="u-width-100 u-margin-top--large"
        onClick={() => {
          const parsed = isObject
            ? updatedFields
            : isArrayOfObjects
              ? updatedArrayFields
              : (() => {
                  try {
                    return JSON.parse(updatedValue);
                  } catch {
                    return updatedValue;
                  }
                })();

          onSubmit(value.name, parsed);
        }}>
        Update
      </Button>
    </AnimatedModal>
  );
};
